import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorBanner from "../components/common/ErrorBanner";
import Card from "../components/common/Card";
import SectionHeader from "../components/common/SectionHeader";
import PrimaryButton from "../components/ui/PrimaryButton";
import apiClient from "../components/utils/apiClient";
import FileTable from "../components/uploads/FileTable";

const SearchBar = ({ placeholder, value, onChange, className = "" }) => (
  <input
    type="text"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`w-full px-4 py-2 border border-gray-300 rounded-md 
      focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
  />
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
    <button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 
        bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Previous
    </button>
    <span className="text-sm text-gray-700">
      Page {currentPage} of {totalPages}
    </span>
    <button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 
        bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Next
    </button>
  </div>
);

const ProjectSearchResults = ({ results, onSelect }) => {
  if (results.length === 0) return null;

  return (
    <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
      <ul className="max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
        {results.map((project) => (
          <li
            key={project.id}
            onClick={() => onSelect(project)}
            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50"
          >
            <div className="flex items-center">
              <span className="block truncate">
                {project.name} (ID: {project.id})
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const WindowSetup = () => {
  const { projectId } = useParams();

  const [windowsData, setWindowsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [maxItems, setMaxItems] = useState(null);
  const [floorList, setFloorList] = useState([]);

  const [windowSearchTerm, setWindowSearchTerm] = useState("");
  const [windowSearchResults, setWindowSearchResults] = useState([]);
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectSearchResults, setProjectSearchResults] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [activeSection, setActiveSection] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userID = localStorage.getItem("userID");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const projectData = await apiClient.get("/api/project_info/", {
          params: { project_id: projectId },
          showToast: false,
          useOperational: false,
        });

        if (projectData && projectData.length > 0) {
          const floorListData = JSON.parse(projectData[0].floor_list || "[]");
          setFloorList(floorListData);
        }

        const windowsData = await apiClient.get(`/api/surfaces`, {
          params: { projectId: projectId, currentPage, limit: 10 },
          showToast: false,
          useOperational: true,
        });

        setWindowsData(windowsData.list || []);
        setTotalPages(windowsData.pages || 1);
        setMaxItems(windowsData.maxItems);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load window data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, currentPage]);

  useEffect(() => {
    const searchProjects = async () => {
      if (projectSearchTerm.length < 2) {
        setProjectSearchResults([]);
        return;
      }

      try {
        const { data } = await apiClient.get("/api/projects_by_name", {
          showToast: false,
          useOperational: true,
        });

        const filteredProjects = data.filter((project) => {
          // Search by name or by ID
          const searchTermLower = projectSearchTerm.toLowerCase();
          const nameMatch = project.name
            .toLowerCase()
            .includes(searchTermLower);
          const idMatch = project.id.toString().includes(projectSearchTerm);

          return nameMatch || idMatch;
        });

        setProjectSearchResults(filteredProjects);
      } catch (err) {
        console.error("Error searching projects:", err);
        setProjectSearchResults([]);
      }
    };

    const debounceTimeout = setTimeout(searchProjects, 300);
    return () => clearTimeout(debounceTimeout);
  }, [projectSearchTerm]);

  useEffect(() => {
    const searchWindows = async () => {
      if (windowSearchTerm.length < 3) {
        setWindowSearchResults([]);
        return;
      }

      try {
        const data = await apiClient.get(`/api/surfaces/search`, {
          params: { projectId: projectId, name: windowSearchTerm },
          showToast: false,
          useOperational: true,
        });

        setWindowSearchResults(data.list || []);
      } catch (err) {
        console.error("Error searching windows:", err);
        setWindowSearchResults([]);
      }
    };

    const debounceTimeout = setTimeout(searchWindows, 300);
    return () => clearTimeout(debounceTimeout);
  }, [windowSearchTerm, projectId]);

  // Handle window deletion
  const handleDeleteWindow = async () => {
    if (!selectedWindow) return;

    setIsSubmitting(true);
    try {
      await apiClient.delete(`/api/surfaces/${selectedWindow.id}`, {
        showToast: false,
        useOperational: true,
      });

      toast.success("Window deleted successfully!");
      setWindowsData(
        windowsData.filter((window) => window.id !== selectedWindow.id)
      );
      setSelectedWindow(null);
    } catch (err) {
      console.error("Error deleting window:", err);
      toast.error("Failed to delete window. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyWindows = async () => {
    if (!selectedProject) {
      toast.error("Please select a project first.");
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await apiClient.post(
        `/api/surfaces/copy?oldProjectId=${selectedProject.id}&newProjectId=${projectId}`,
        {
          sourceProjectId: selectedProject.id,
          targetProjectId: projectId,
        },
        {
          showToast: false,
          useOperational: true,
        }
      );

      toast.success("Windows copied successfully!");
      setProjectSearchTerm("");
      setSelectedProject(null);
      setActiveSection("");

      const refreshedData = await apiClient.get(`/api/surfaces`, {
        params: { projectId: projectId, currentPage: 1 },
        showToast: false,
        useOperational: true,
      });

      setWindowsData(refreshedData.list || []);
      setTotalPages(refreshedData.pages || 1);
      setMaxItems(refreshedData.maxItems);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error copying windows:", err);
      toast.error("Failed to copy windows. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setProjectSearchTerm(`${project.name} (ID: ${project.id})`);
    setProjectSearchResults([]);
  };

  if (loading && windowsData.length === 0) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-t-blue-500 border-b-blue-500 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Loading window data...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Digital Twin Window Manager - Project ${projectId}`}
            subtitle="Manage and configure window settings for your digital twin"
          >
            {floorList.length > 0 && (
              <div className="px-6 py-2 text-sm text-gray-600 bg-gray-50 border-t border-b border-gray-200">
                Available floors: {floorList.join(", ")}
              </div>
            )}

            <ErrorBanner message={error} />

            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <SearchBar
                      placeholder="Search windows by name (min 3 chars)"
                      value={windowSearchTerm}
                      onChange={setWindowSearchTerm}
                    />
                  </div>
                  <div className="md:w-64">
                    <select
                      value={selectedWindow ? selectedWindow.id : ""}
                      onChange={(e) => {
                        const window = windowSearchResults.find(
                          (w) => w.id === parseInt(e.target.value)
                        );
                        setSelectedWindow(window);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md 
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a window</option>
                      {windowSearchResults.map((window) => (
                        <option key={window.id} value={window.id}>
                          {window.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedWindow && (
                    <PrimaryButton
                      onClick={handleDeleteWindow}
                      isLoading={isSubmitting}
                      loadingText="Deleting..."
                      className="bg-red-600 hover:bg-red-700"
                      size="md"
                      width="auto"
                    >
                      Delete Window
                    </PrimaryButton>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
                <SectionHeader
                  title={`Windows Overview ${
                    maxItems ? `(Total: ${maxItems})` : ""
                  }`}
                  className="px-6 py-4 bg-gray-50 border-b border-gray-200"
                />

                <FileTable
                  files={windowsData.map((window) => ({
                    id: window.id,
                    name: window.name,
                    type: `${window.area} m²`,
                    _created_at: null,
                    _updated_at: window.level || "N/A",
                  }))}
                  selectedFiles={[]}
                  onSelectFile={() => {}}
                  onSelectAll={() => {}}
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>

              <div className="mt-6">
                <PrimaryButton
                  onClick={() =>
                    setActiveSection(activeSection === "copy" ? "" : "copy")
                  }
                >
                  {activeSection === "copy"
                    ? "Hide Copy Options"
                    : "Copy Windows from Another Project"}
                </PrimaryButton>
              </div>

              {activeSection === "copy" && (
                <div className="mt-6 bg-gray-50 rounded-lg p-6">
                  <SectionHeader title="Copy Windows Configuration" />

                  <div className="space-y-4">
                    <div className="relative">
                      <SearchBar
                        placeholder="Search for a project by name or ID (min 2 chars)"
                        value={projectSearchTerm}
                        onChange={setProjectSearchTerm}
                      />

                      <ProjectSearchResults
                        results={projectSearchResults}
                        onSelect={handleSelectProject}
                      />
                    </div>

                    {selectedProject && (
                      <div className="flex items-center justify-between bg-blue-50 p-4 rounded-md">
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            Selected Project:
                          </p>
                          <p className="text-lg text-blue-700">
                            {selectedProject.name} (ID: {selectedProject.id})
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedProject(null);
                            setProjectSearchTerm("");
                          }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Change
                        </button>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <PrimaryButton
                        onClick={handleCopyWindows}
                        isLoading={isSubmitting}
                        loadingText="Copying..."
                        disabled={!selectedProject}
                        width="auto"
                      >
                        Copy Windows
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default WindowSetup;
